// Type-only imports on purpose: this module carries the display-failure reporting policy, and its
// unit test loads it under bare `node --test`, where the `@cineamo/api/...` extension-less
// specifiers would not resolve. Keep every import here erasable.
import type { AdPlacement } from '@cineamo/api/models/advertisement/AdPlacement.enum';
import type { AdvertisementDto } from '@cineamo/api/models/advertisement/AdvertisementDto.types';
import type { ContentDto } from '@cineamo/api/models/content/ContentDto.types';
import type { MovieDto } from '@cineamo/api/models/movie/v2/MovieDto.types';

import type { ApiErrorFallback, ApiErrorLike } from '@cineamo/api/types/ApiErrorResponse.types';

import type { GetAdvertisementDisplayParameter } from '@cineamo/api/resources/advertisement/advertisement-api';

// The `/advertisements/display` endpoint returns a reduced "AdvertisementMinimum" shape (see
// backend AdvertisementMinimumDto), NOT the full AdvertisementDto — it has no `variants` array,
// `impressionCount`/`clickCount`, `cinemaIds`, etc. The selected A/B variant's creative fields
// (or the campaign's own, when no variant applies — legacy/single-creative ads) are already
// substituted server-side; `variantId` identifies which variant was served, for tracking.
export type AdvertisementDisplayData = Pick<
    AdvertisementDto,
    | 'id'
    | 'adCategory'
    | 'advertiserName'
    | 'advertiserImageUrl'
    | 'startDatetime'
    | 'endDatetime'
    | 'clickThroughUrl'
    | 'topBannerImageRef'
    | 'topBannerImageUrl'
    | 'topBannerDesktopImageRef'
    | 'topBannerDesktopImageUrl'
    | 'layerImageRef'
    | 'layerImageUrl'
    | 'placement'
    | 'status'
    | 'displayLocations'
    | '_embedded'
> & {
    variantId?: number;
};

export type AdvertisementPublicData = Pick<
    AdvertisementDisplayData,
    | 'id'
    | 'adCategory'
    | 'advertiserName'
    | 'advertiserImageUrl'
    | 'clickThroughUrl'
    | 'topBannerImageUrl'
    | 'topBannerDesktopImageUrl'
    | 'layerImageUrl'
    | 'placement'
    | 'status'
    | 'endDatetime'
    | 'variantId'
> & {
    // Only present for film-linked ads (adCategory === 'film'); external ads have no movie content.
    contentId?: ContentDto['id'];
    contentName?: ContentDto['name'];
    movieId?: MovieDto['id'];
};

export function getAdvertisementPublicData(
    advertisement: AdvertisementDisplayData | null
): AdvertisementPublicData | null {
    if (!advertisement) {
        return null;
    }
    return {
        id: advertisement.id,
        adCategory: advertisement.adCategory,
        advertiserName: advertisement.advertiserName,
        advertiserImageUrl: advertisement.advertiserImageUrl,
        clickThroughUrl: advertisement.clickThroughUrl,
        topBannerImageUrl: advertisement.topBannerImageUrl,
        topBannerDesktopImageUrl: advertisement.topBannerDesktopImageUrl,
        layerImageUrl: advertisement.layerImageUrl,
        placement: advertisement.placement,
        status: advertisement.status,
        contentId: advertisement._embedded?.movieCopyright?._embedded?.content?.id,
        contentName: advertisement._embedded?.movieCopyright?._embedded?.content?.name,
        movieId: advertisement._embedded?.movieCopyright?._embedded?.content?.movieId,
        // needed for per-variant impression/click attribution
        variantId: advertisement.variantId,
        // needed for cookies expiration
        endDatetime: advertisement.endDatetime
    };
}

export const COOKIE_NAME_ADVERTISEMENT_CLOSED = (id: AdvertisementPublicData['id'], placement: AdPlacement) =>
    `advertisement_closed_${id}-${placement}`;

/**
 * What `getAdvertisementDisplay` resolves with once a caller has swallowed its rejection with `orError`: besides
 * the ad — or `null` when there is none — the fetch client's error envelope for a non-2xx response, or a bare
 * transport `Error`. Spelling that out is what forces callers to narrow with {@link isAdvertisementPublicData}
 * instead of the old `'isError' in result` test, which a fetch `TypeError` passed straight through as if it were
 * an advertisement.
 */
export type AdvertisementDisplayResult = AdvertisementPublicData | null | ApiErrorLike | ApiErrorFallback | Error;

export function isAdvertisementPublicData(value: unknown): value is AdvertisementPublicData {
    return (
        !!value &&
        typeof value === 'object' &&
        !(value instanceof Error) &&
        typeof (value as AdvertisementPublicData).id === 'number'
    );
}

/**
 * Fingerprint discriminator for a failed display request: the HTTP status, or `network` when the
 * request never produced one (DNS/TLS failure, abort, unparseable body).
 */
export const ADVERTISEMENT_DISPLAY_NETWORK_STATUS_KEY = 'network';

export const ADVERTISEMENT_DISPLAY_OAUTH_TOKEN_MISSING_KEY = 'oauth-token-missing';

export const ADVERTISEMENT_DISPLAY_REPORT_INTERVAL_MS = 15 * 60 * 1000;

const lastReportedAt = new Map<string, number>();

/**
 * Time-based rate gate, one event per key per interval. It has to be time-based rather than
 * once-per-process: the display request fires once per provider mount (one server-action call per
 * page load), and the serverless instances that serve those calls churn at an unmeasured rate, so a
 * "seen this already" set caps events at (instances x keys) — i.e. not at all. This caps the *rate*
 * per instance; it does not put a ceiling on the total.
 */
export function shouldReportAdvertisementDisplayEvent(key: string, now: number = Date.now()): boolean {
    const previous = lastReportedAt.get(key);
    if (undefined !== previous && now - previous < ADVERTISEMENT_DISPLAY_REPORT_INTERVAL_MS) {
        return false;
    }

    lastReportedAt.set(key, now);
    return true;
}

export function resetAdvertisementDisplayReporting(): void {
    lastReportedAt.clear();
}

export type AdvertisementDisplayFailureReport = {
    message: string;
    tags: Record<string, string>;
    extra: Record<string, unknown>;
    fingerprint: string[];
    level: 'error';
};

/**
 * Turns a non-advertisement result into the Sentry payload to report, or `null` when it must not be
 * reported (an ad, no ad, or a key already reported inside the current interval). The decision and
 * the payload live here so the two apps that own the Sentry SDK cannot drift; they only make the
 * `captureException` call.
 *
 * "Nothing booked for this surface right now" is a 204 since CIN-15620 and resolves to `null`
 * before it gets here, so every non-2xx — a contract skew that 400s, a 404 (the route is gone or
 * the API still answers the pre-CIN-15620 way), a 5xx, a transport fault — means the channel
 * delivered zero ads for a reason nobody chose, which is revenue-bearing and stays at error
 * level. Volume is bounded by {@link shouldReportAdvertisementDisplayEvent}, not by lowering
 * severity.
 *
 * `errorResponse` is pre-serialized: the validation body sits at `extra.errorResponse.parameters
 * .<field>`, which is exactly one level past Sentry's default `normalizeDepth` of 3 — that is why
 * the original 11.7k events showed the causal field as "[Object]" and could not be triaged.
 */
export function buildAdvertisementDisplayFailureReport(
    params: GetAdvertisementDisplayParameter,
    result: AdvertisementDisplayResult,
    now: number = Date.now()
): AdvertisementDisplayFailureReport | null {
    if (null === result || isAdvertisementPublicData(result)) {
        return null;
    }

    // An ApiError is an Error too but has a status, so no `instanceof Error` test here.
    const status = 'status' in result && typeof result.status === 'number' ? result.status : undefined;
    const statusKey = undefined !== status ? String(status) : ADVERTISEMENT_DISPLAY_NETWORK_STATUS_KEY;

    if (!shouldReportAdvertisementDisplayEvent(statusKey, now)) {
        return null;
    }

    return {
        message: `Advertisement display API error: ${statusKey}`,
        tags: {
            component: 'advertisement-display',
            status: statusKey,
            endpoint: '/advertisements/display'
        },
        extra: {
            params,
            errorResponse: serializeAdvertisementDisplayError(result),
            statusCode: status ?? null
        },
        fingerprint: ['advertisement-display', statusKey],
        level: 'error'
    };
}

function serializeAdvertisementDisplayError(result: ApiErrorLike | ApiErrorFallback | Error): string {
    if (result instanceof Error && !('status' in result)) {
        return `${result.name}: ${result.message}`;
    }

    try {
        return JSON.stringify(('data' in result ? result.data : undefined) ?? result);
    } catch {
        return String(result);
    }
}
